import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  MagnifyingGlass, Plus, DotsThreeVertical, PencilSimple, Archive, Trash, ArrowsClockwise,
  DownloadSimple, FilePdf, FileXls, User, Funnel, X,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const GENDERS = ["Male", "Female", "Other"];

const empty = {
  student_number: "", full_name: "", date_of_birth: "", gender: "", nationality: "",
  address: "", phone: "", email: "", school: "", grade: "", placement_level: "",
  english_level: "", medical_information: "", notes: "",
  guardian_name: "", guardian_phone: "", guardian_email: "", guardian_relation: "",
  emergency_contact_name: "", emergency_contact_phone: "", photo: "",
};

export function StudentForm({ initial, onSave, onClose }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ ...empty, ...(initial || {}) });
  const [saving, setSaving] = useState(false);

  const setV = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!f.full_name) return toast.error(t("students.toasts.fullNameRequired"));
    setSaving(true);
    try {
      if (initial?.id) {
        const { data } = await api.put(`/students/${initial.id}`, f);
        toast.success(t("students.toasts.studentUpdated")); onSave(data);
      } else {
        const { data } = await api.post("/students", f);
        toast.success(t("students.toasts.studentCreated")); onSave(data);
      }
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const field = (label, key, type = "text", placeholder = "") => (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
      <Input type={type} value={f[key] || ""} onChange={setV(key)} placeholder={placeholder} className="mt-1.5 h-10" />
    </div>
  );

  return (
    <Tabs defaultValue="personal" className="mt-2">
      <TabsList className="w-full justify-start">
        <TabsTrigger value="personal">{t("students.tabs.personal")}</TabsTrigger>
        <TabsTrigger value="academic">{t("students.tabs.academic")}</TabsTrigger>
        <TabsTrigger value="guardian">{t("students.tabs.guardian")}</TabsTrigger>
        <TabsTrigger value="medical">{t("students.tabs.medical")}</TabsTrigger>
      </TabsList>

      <TabsContent value="personal" className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
        {field(t("students.fields.fullNameStar"), "full_name")}
        {field(t("students.fields.studentNumber"), "student_number", "text", t("students.fields.studentNumberPh"))}
        {field(t("students.fields.dateOfBirth"), "date_of_birth", "date")}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">{t("students.fields.gender")}</Label>
          <Select value={f.gender || ""} onValueChange={setV("gender")}>
            <SelectTrigger className="mt-1.5 h-10"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Male">{t("common.male")}</SelectItem>
              <SelectItem value="Female">{t("common.female")}</SelectItem>
              <SelectItem value="Other">{t("common.other")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {field(t("students.fields.nationality"), "nationality")}
        {field(t("students.fields.phone"), "phone", "tel")}
        {field(t("students.fields.email"), "email", "email")}
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">{t("students.fields.address")}</Label>
          <Textarea value={f.address || ""} onChange={setV("address")} rows={2} className="mt-1.5" />
        </div>
      </TabsContent>

      <TabsContent value="academic" className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
        {field(t("students.fields.school"), "school")}
        {field(t("students.fields.grade"), "grade")}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">{t("students.fields.placementLevel")}</Label>
          <Select value={f.placement_level || ""} onValueChange={setV("placement_level")}>
            <SelectTrigger className="mt-1.5 h-10"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
            <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">{t("students.fields.englishLevel")}</Label>
          <Select value={f.english_level || ""} onValueChange={setV("english_level")}>
            <SelectTrigger className="mt-1.5 h-10"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
            <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {field(t("students.fields.photoUrl"), "photo")}
      </TabsContent>

      <TabsContent value="guardian" className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
        {field(t("students.fields.guardianName"), "guardian_name")}
        {field(t("students.fields.guardianRelation"), "guardian_relation")}
        {field(t("students.fields.guardianPhone"), "guardian_phone", "tel")}
        {field(t("students.fields.guardianEmail"), "guardian_email", "email")}
        {field(t("students.fields.emergencyContactName"), "emergency_contact_name")}
        {field(t("students.fields.emergencyContactPhone"), "emergency_contact_phone", "tel")}
      </TabsContent>

      <TabsContent value="medical" className="space-y-4 pt-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">{t("students.fields.medicalInfo")}</Label>
          <Textarea value={f.medical_information || ""} onChange={setV("medical_information")} rows={3} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">{t("students.fields.notes")}</Label>
          <Textarea value={f.notes || ""} onChange={setV("notes")} rows={4} className="mt-1.5" />
        </div>
      </TabsContent>

      <DialogFooter className="mt-6">
        <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
        <Button data-testid="student-form-save" onClick={submit} disabled={saving}>
          {saving ? t("common.saving") : (initial?.id ? t("students.buttons.saveChanges") : t("students.buttons.createStudent"))}
        </Button>
      </DialogFooter>
    </Tabs>
  );
}

export default function Students() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("");
  const [gender, setGender] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (level) params.level = level;
      if (gender) params.gender = gender;
      if (showArchived) params.archived = true;
      const { data } = await api.get("/students", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const onSearch = () => load();

  const archive = async (id) => {
    try { await api.post(`/students/${id}/archive`); toast.success(t("students.toasts.archived")); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const restore = async (id) => {
    try { await api.post(`/students/${id}/restore`); toast.success(t("students.toasts.restored")); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("students.toasts.confirmDelete"))) return;
    try { await api.delete(`/students/${id}`); toast.success(t("students.toasts.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCSV = () => {
    const cols = ["student_number", "full_name", "email", "phone", "gender", "nationality", "english_level", "status"];
    const header = cols.join(",");
    const body = rows.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const csv = header + "\n" + body;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "students.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported CSV");
  };

  const exportPDF = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const html = `<html><head><title>Students</title><style>
      body{font-family:system-ui;padding:24px;color:#111}
      h1{margin:0 0 16px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border-bottom:1px solid #ddd;padding:8px;text-align:left}
      th{background:#0A1128;color:#fff}
    </style></head><body>
      <h1>Bilinguist — Students</h1>
      <table><thead><tr>
        <th>#</th><th>Name</th><th>Email</th><th>Phone</th><th>Level</th><th>Status</th>
      </tr></thead><tbody>
        ${rows.map((r) => `<tr><td>${r.student_number || ""}</td><td>${r.full_name}</td><td>${r.email || ""}</td><td>${r.phone || ""}</td><td>${r.english_level || ""}</td><td>${r.status}</td></tr>`).join("")}
      </tbody></table>
      <script>window.print()</script>
    </body></html>`;
    w.document.write(html); w.document.close();
  };

  const kpis = useMemo(() => ({
    total: rows.length,
    active: rows.filter((r) => r.status === "active").length,
    archived: rows.filter((r) => r.archived).length,
  }), [rows]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.students")}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("students.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("students.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-csv-btn" variant="outline" onClick={exportCSV} className="gap-2 rounded-full">
            <FileXls size={16} /> Excel
          </Button>
          <Button data-testid="export-pdf-btn" variant="outline" onClick={exportPDF} className="gap-2 rounded-full">
            <FilePdf size={16} /> PDF
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-student-btn" onClick={() => setEditing(null)} className="gap-2 rounded-full">
                <Plus size={16} weight="bold" /> {t("students.addStudent")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Manrope" }}>
                  {editing ? t("students.editStudent") : t("students.newStudent")}
                </DialogTitle>
              </DialogHeader>
              <StudentForm initial={editing} onSave={load} onClose={() => setOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-border/60">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="students-search"
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearch()}
              placeholder={t("students.filters.searchPlaceholder")}
              className="pl-10 h-10"
            />
          </div>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="w-full md:w-40 h-10"><SelectValue placeholder={t("students.filters.level")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("students.filters.allLevels")}</SelectItem>
              {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={gender} onValueChange={setGender}>
            <SelectTrigger className="w-full md:w-40 h-10"><SelectValue placeholder={t("students.filters.gender")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("students.filters.allGenders")}</SelectItem>
              <SelectItem value="Male">{t("common.male")}</SelectItem>
              <SelectItem value="Female">{t("common.female")}</SelectItem>
              <SelectItem value="Other">{t("common.other")}</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Button onClick={onSearch} className="rounded-full gap-2"><Funnel size={14} /> {t("students.filters.apply")}</Button>
            <Button variant="ghost" onClick={() => { setQ(""); setLevel(""); setGender(""); setTimeout(load, 0); }} className="rounded-full">
              <X size={14} />
            </Button>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            {t("students.filters.archivedOnly")}
          </label>
        </CardContent>
      </Card>

      {/* KPI bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-4 rounded-md border border-border/60 bg-card">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("students.kpi.showing")}</div>
          <div className="text-2xl font-bold font-mono">{kpis.total}</div>
        </div>
        <div className="p-4 rounded-md border border-border/60 bg-card">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("students.kpi.active")}</div>
          <div className="text-2xl font-bold font-mono text-emerald-600 dark:text-emerald-400">{kpis.active}</div>
        </div>
        <div className="p-4 rounded-md border border-border/60 bg-card">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("students.kpi.archived")}</div>
          <div className="text-2xl font-bold font-mono text-muted-foreground">{kpis.archived}</div>
        </div>
      </div>

      {/* Table */}
      <Card className="border-border/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="students-table">
            <thead className="bg-secondary/40 border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{t("students.table.student")}</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{t("students.table.id")}</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{t("students.table.contact")}</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{t("students.table.level")}</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{t("students.table.nationality")}</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{t("students.table.status")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">{t("students.table.loading")}</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">{t("students.table.empty")}</td></tr>
              )}
              {!loading && rows.map((s) => (
                <tr key={s.id} data-testid={`student-row-${s.id}`}
                    onClick={() => nav(`/students/${s.id}`)}
                    className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0">
                        {s.photo ? <img src={s.photo} alt="" className="w-full h-full rounded-full object-cover" /> : s.full_name?.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{s.full_name}</div>
                        <div className="text-[11px] text-muted-foreground">{s.gender} · {s.grade}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{s.student_number}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs">{s.email}</div>
                    <div className="text-[11px] text-muted-foreground">{s.phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    {s.english_level && (
                      <Badge variant="secondary" className="font-mono">{s.english_level}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{s.nationality}</td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      "text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider",
                      s.status === "active" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
                        "bg-muted text-muted-foreground"
                    )}>{s.status === "active" ? t("students.status.active") : s.status === "archived" ? t("students.status.archived") : s.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`row-menu-${s.id}`} className="w-8 h-8 rounded-md hover:bg-secondary flex items-center justify-center">
                          <DotsThreeVertical size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => nav(`/students/${s.id}`)}>
                          <User size={14} className="mr-2" /> {t("students.row.viewProfile")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditing(s); setOpen(true); }}>
                          <PencilSimple size={14} className="mr-2" /> {t("students.row.edit")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {s.archived ? (
                          <DropdownMenuItem onClick={() => restore(s.id)}>
                            <ArrowsClockwise size={14} className="mr-2" /> {t("students.row.restore")}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => archive(s.id)}>
                            <Archive size={14} className="mr-2" /> {t("students.row.archive")}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => remove(s.id)} className="text-destructive">
                          <Trash size={14} className="mr-2" /> {t("students.row.delete")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
